"use client";

import React from "react";
import { Loader, Check } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function QuoteForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in duration-500">
        <div className="size-16 bg-accent/10 text-accent rounded-lg flex items-center justify-center mb-6">
          <Check className="size-8" />
        </div>
        <h3 className="text-2xl font-default font-semibold text-foreground mb-2">Subscription Confirmed</h3>
        <p className="text-muted-foreground mb-8 max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="rounded-md">
          Back to Form
        </Button>
      </div>;
  }
  const inputMinimalStyle = "border-0 border-b border-border rounded-sm shadow-none focus-visible:ring-0 focus-visible:border-b-accent focus-visible:border-b-[1px] px-2 bg-transparent transition-colors";
  const interestOptions = [{
    id: "pruning",
    label: "Seasonal Pruning Tips"
  }, {
    id: "health",
    label: "Tree Health & Disease Alerts"
  }, {
    id: "planting",
    label: "Planting & Selection Guides"
  }, {
    id: "emergency",
    label: "Storm Prep & Emergency Care"
  }];
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="first_name" rules={{
        required: "First name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground">First Name</FormLabel>
              <FormControl>
                <Input placeholder="John" className={inputMinimalStyle} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className={inputMinimalStyle} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="space-y-4">
        <FormLabel className="text-muted-foreground">Interests (Select all that apply)</FormLabel>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {interestOptions.map((option, i) => <FormField key={option.id} control={control} name="interests" render={({
          field
        }) => {
          const value = Array.isArray(field.value) ? field.value : [];
          return <FormItem data-index={i} className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <input type="checkbox" className="size-4 mt-1 rounded border-border text-accent focus:ring-accent" checked={value.includes(option.id)} onChange={e => {
                const newValue = e.target.checked ? [...value, option.id] : value.filter((v: string) => v !== option.id);
                field.onChange(newValue);
              }} />
                    </FormControl>
                    <FormLabel className="font-normal cursor-pointer">
                      {option.label}
                    </FormLabel>
                  </FormItem>;
        }} />)}
        </div>
      </div>

      <FormField control={control} name="consent" rules={{
      required: "You must agree to receive updates"
    }} render={({
      field
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0">
            <FormControl>
              <input type="checkbox" className="size-4 mt-1 rounded border-border text-accent focus:ring-accent" checked={field.value} onChange={field.onChange} />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="font-normal cursor-pointer">
                I agree to receive seasonal tree care insights and service updates from Arbor Pro.
              </FormLabel>
              <FormMessage />
            </div>
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-destructive text-sm">{submitError}</p>
        </div>}

      <div className="pt-4 flex justify-center">
        <Button type="submit" disabled={isSubmitting} size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 min-w-[200px] rounded-md shadow-md">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-5" />
              Joining...
            </> : 'Join Our Network'}
        </Button>
      </div>
    </Form>;
}
export default function GetYourFreeTreeCareQuote() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="get-your-free-tree-care-quote" className="bg-background py-24 relative overflow-hidden">
      <div ref={ref} className={`max-w-[800px] mx-auto px-4 sm:px-6 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="flex flex-col items-center text-center mb-12">
          <div className="w-[40px] h-1 bg-accent mb-6 rounded-sm"></div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight">
            Stay Informed
          </h2>
          <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
            Join our network of DFW homeowners to receive seasonal tree care insights, storm alerts, and exclusive service updates from our certified arborists.
          </p>
        </div>

        <Card className="bg-card border-none shadow-xl rounded-xl overflow-hidden">
          <CardContent className="p-6 sm:p-10 md:p-12">
            <FormProvider formId="stay_informed_form_tree_insights" formKey="stay_informed_form_tree_insights" sectionName="GetYourFreeTreeCareQuote" defaultValues={{
            first_name: "",
            email: "",
            interests: [],
            consent: false
          }} submitText="Join Our Network" successMessage="Thank you for joining! You'll receive seasonal tree care insights and service updates from our team.">
              <QuoteForm />
            </FormProvider>
          </CardContent>
        </Card>
      </div>
    </section>;
}